import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { ROUTES } from '../../constants/routes';
import { useListCitiesQuery } from '../../store/siteContentApi';

const CitiesWeServe = () => {
  const navigate = useNavigate();
  const { data: cities = [], isLoading } = useListCitiesQuery();

  // Nothing to show while the first fetch is in-flight or if admin has
  // hidden every city — keep the home page tidy rather than rendering
  // an empty grid.
  if (isLoading || cities.length === 0) return null;

  return (
    <Box
      id="where-we-operate"
      sx={{
        bgcolor: 'var(--parchment)',
        py: { xs: 7, md: 10 },
        // Offset so footer "Find a Centre" deep-link doesn't park the
        // section heading behind the sticky navbar (≈64 xs / 80 md).
        scrollMarginTop: { xs: 72, md: 96 },
      }}
    >
      <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
        {/* Centered header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.25,
              mb: 1.75,
              lineHeight: 1,
            }}
          >
            <Box sx={{ width: 24, height: '1.5px', bgcolor: 'var(--sage)' }} />
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--sage)',
                lineHeight: 1,
              }}
            >
              Where We Operate
            </Typography>
          </Box>
          <Typography
            className="serif"
            sx={{
              fontSize: { xs: 24, sm: 28, md: 32, lg: 36 },
              fontWeight: 500,
              color: 'var(--ink)',
              lineHeight: 1.15,
              mb: 1.5,
            }}
          >
            Now in {cities.length} {cities.length === 1 ? 'city' : 'cities'}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 13, md: 14 },
              color: 'var(--stone)',
              fontWeight: 300,
              maxWidth: 560,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Home collection and walk-in centres across Telangana, Andhra Pradesh, Karnataka, UP and Jammu & Kashmir.
          </Typography>
        </Box>

        {/* 4-col city grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 2,
            width: '100%',
          }}
        >
          {cities.map((c) => {
            const gradFrom = c.gradient_from || '#0F3A2C';
            const gradTo = c.gradient_to || '#3D7A52';
            const hasImage = !!c.image_url;
            return (
              <Box
                key={c._id || c.code}
                // Pass the city name as a query param so /centres can
                // pre-select the matching tab. Encoding handles spaces
                // / special chars in city names.
                onClick={() => navigate(`${ROUTES.CENTRES}?city=${encodeURIComponent(c.name)}`)}
                sx={{
                  borderRadius: '16px',
                  overflow: 'hidden',
                  bgcolor: '#fff',
                  border: '1px solid var(--border-l)',
                  cursor: 'pointer',
                  transition: 'all 0.35s cubic-bezier(0.23,1,0.32,1)',
                  boxShadow: '0 4px 20px rgba(21,42,30,0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 48px rgba(21,42,30,0.1)',
                    borderColor: 'var(--sage-p)',
                  },
                }}
              >
                {/* Banner — image when admin-set, gradient otherwise.
                    The watermark wordmark + badge sit on top either way;
                    a soft dark scrim is added under the image only so
                    text stays legible against bright photos. */}
                <Box
                  sx={{
                    height: 130,
                    position: 'relative',
                    overflow: 'hidden',
                    ...(hasImage
                      ? {
                          backgroundImage: `url("${c.image_url}")`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }
                      : {
                          background: `linear-gradient(135deg, ${gradFrom} 0%, ${gradTo} 100%)`,
                        }),
                  }}
                >
                  {hasImage && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%)',
                        pointerEvents: 'none',
                      }}
                    />
                  )}

                  {/* City wordmark watermark */}
                  <Typography
                    className="serif"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      left: 12,
                      fontSize: 36,
                      fontWeight: 600,
                      fontStyle: 'italic',
                      color: hasImage ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.25)',
                      lineHeight: 1,
                      letterSpacing: '-0.02em',
                      textShadow: hasImage ? '0 1px 6px rgba(0,0,0,0.4)' : 'none',
                    }}
                  >
                    {c.name}
                  </Typography>

                  {/* Badge */}
                  {c.badge && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        bgcolor: 'rgba(255,255,255,0.18)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        borderRadius: 100,
                        px: 1.125,
                        py: 0.375,
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#fff',
                        letterSpacing: '0.04em',
                        lineHeight: 1,
                      }}
                    >
                      {c.badge}
                    </Box>
                  )}
                </Box>

                {/* Info */}
                <Box sx={{ p: '12px 14px 14px' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', mb: 0.5, lineHeight: 1.3 }}>
                    {c.name}
                  </Typography>
                  {/* Landmark on the left, Live pill pushed to the right.
                      Flex row with justify-between prevents the two from
                      colliding when the landmark text is long. */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      lineHeight: 1,
                    }}
                  >
                    {c.landmark ? (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <Box component="span" sx={{ fontSize: 11, lineHeight: 1, flexShrink: 0 }}>📍</Box>
                        <Typography
                          component="span"
                          noWrap
                          sx={{
                            fontSize: 11,
                            color: 'var(--sage)',
                            fontWeight: 500,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {c.landmark}
                        </Typography>
                      </Box>
                    ) : <Box sx={{ flex: 1 }} />}
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: '#22C55E',
                          animation: 'blink 1.4s ease-in-out infinite',
                        }}
                      />
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#22C55E',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          lineHeight: 1,
                        }}
                      >
                        Live
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* CTA */}
        <Box sx={{ textAlign: 'center', mt: 3.5 }}>
          <Button
            onClick={() => navigate(ROUTES.CENTRES)}
            sx={{
              borderRadius: 100,
              border: '1.5px solid var(--border)',
              bgcolor: '#fff',
              color: 'var(--ink)',
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              px: 2.5,
              py: 1,
              '&:hover': { borderColor: 'var(--sage)', bgcolor: 'var(--cream)' },
            }}
          >
            View all centres & directions →
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default CitiesWeServe;
